// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getPaymentTerms } from '@/payment-term/api/get-payment-terms';
import { PaymentTerm } from '@/payment-term/api/types';
import { PaymentTermAutocomplete } from '@/payment-term/components/payment-term-autocomplete';
import { useQuery } from '@tanstack/react-query';
import { ForwardedRef, forwardRef } from 'react';

interface Props {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If true, the input is disabled.
   */
  disabled?: boolean;

  /**
   * The currently selected payment term.
   */
  value: PaymentTerm | null;

  /**
   * A callback fired when the selected payment term changes.
   */
  onChange: (value: PaymentTerm | null) => void;
}

/**
 * Renders a payment term autocomplete component with a list of shipping
 * payment terms. This function also forwards a ref to the autocomplete
 * component.
 */
function PaymentTermSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { data } = useQuery({
    queryKey: ['payment-term'],
    queryFn: getPaymentTerms,
  });

  const options = data?.paymentTerms ?? [];

  const handleChange = (_: unknown, value: PaymentTerm | null) => {
    props.onChange(value);
  };

  return (
    <PaymentTermAutocomplete
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      options={options}
      onChange={handleChange}
      helperText={props.helperText}
      disabled={props.disabled}
    />
  );
}

/**
 * A controlled component for selecting a payment term from a list of
 * options.
 */
export const PaymentTermSearch = forwardRef(PaymentTermSearchRenderer);
